import { toast } from "react-toastify";
import { TOAST_MESSAGES } from './constants';
import { errorMessage } from '../../store/restClient';
import { FileUploadState, MultipleFileUploadState, UseCaseMode, FileStates } from './types';

// ---------------------------
// Utility Functions
// ---------------------------
export const emptyFileState = (): FileUploadState => ({
  file: null,
  fileName: "",
  isValid: false,
});

export const emptyTtlState = (): MultipleFileUploadState => ({ files: [] });

export const createEmptyFileStates = (): FileStates => ({
  rdf: emptyTtlState(),
  json: emptyFileState(),
  ttl: emptyTtlState(),
  xml: emptyTtlState(),
  excel: emptyFileState(),
});

export const readAsArrayBuffer = (file: File): Promise<ArrayBuffer> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(file);
  });

export const formatModeText = (mode: UseCaseMode): string => mode.replace(/_/g, " ");

export const getNextCustomTestName = (existingNames: string[] = []): string => {
  const prefix = "Custom Test Session ";
  let n = 1;
  const taken = new Set(existingNames);
  while (taken.has(`${prefix}${n}`)) n++;
  return `${prefix}${n}`;
};

export const validateFileExtension = (fileName: string, allowedExtensions: string[]): boolean =>
  allowedExtensions.some(ext => fileName.toLowerCase().endsWith(`.${ext}`));

export const createFormData = (customTestName: string): FormData => {
  const formData = new FormData();
  formData.append("ProfileName", "CustomProfile");
  formData.append("CustomTestName", customTestName);
  return formData;
};

export const getNcdFileNames = (sessionIdentifier: string): { urlName: string; displayName: string } => {
  const datePart = sessionIdentifier.split('_').find(p => /^\d{14}$/.test(p));

  if (datePart) {
    const utcTs = `${datePart.slice(0, 4)}-${datePart.slice(4, 6)}-${datePart.slice(6, 8)}_${datePart.slice(8, 10)}${datePart.slice(10, 12)}${datePart.slice(12, 14)}`;
    const urlName = `DNV-CIMbion_SchemaRules_${utcTs}.ttl`;

    const utcDate = new Date(Date.UTC(
      parseInt(datePart.slice(0, 4)),
      parseInt(datePart.slice(4, 6)) - 1,
      parseInt(datePart.slice(6, 8)),
      parseInt(datePart.slice(8, 10)),
      parseInt(datePart.slice(10, 12)),
      parseInt(datePart.slice(12, 14)),
    ));
    const pad = (n: number) => String(n).padStart(2, '0');
    const localTs = `${utcDate.getFullYear()}-${pad(utcDate.getMonth() + 1)}-${pad(utcDate.getDate())}_${pad(utcDate.getHours())}${pad(utcDate.getMinutes())}${pad(utcDate.getSeconds())}`;
    const displayName = `DNV-CIMbion_SchemaRules_${localTs}.ttl`;

    return { urlName, displayName };
  }

  const n = new Date();
  const pad = (v: number) => String(v).padStart(2, '0');
  const ts = `${n.getFullYear()}-${pad(n.getMonth() + 1)}-${pad(n.getDate())}_${pad(n.getHours())}${pad(n.getMinutes())}${pad(n.getSeconds())}`;
  const name = `DNV-CIMbion_SchemaRules_${ts}.ttl`;
  return { urlName: name, displayName: name };
};

export const createFileFromArrayBuffer = (arrayBuffer: ArrayBuffer, fileName: string, fileType?: string): File =>
  new File([arrayBuffer], fileName, { type: fileType });

export const handleApiError = (error: any, operationName: string): void => {
  if (error instanceof DOMException && error.name === 'AbortError') return;
  console.error(`${operationName} error:`, error);
  // Show what actually failed; the same text from restClient collapses into one toast by its id
  const message = error?.response
    ? errorMessage(error)
    : (error instanceof Error && error.message) || TOAST_MESSAGES.ERROR_FAILED_TO_RUN;
  toast.error(message, { toastId: message });
};

export const showSuccessToast = (message: string, sessionName?: string): void => {
  const fullMessage = sessionName ? `${message} File saved in session ${sessionName}` : message;
  toast.success(fullMessage);
};

// ---------------------------
// File Processing Utilities
// ---------------------------
export const processFileUpload = async (
  file: File,
  allowedExtensions: string[],
  label: string
): Promise<FileUploadState | null> => {
  if (!validateFileExtension(file.name, allowedExtensions)) {
    toast.error(`${TOAST_MESSAGES.PLEASE_UPLOAD_VALID_FILE} ${label}`);
    return null;
  }

  try {
    const arrayBuffer = await readAsArrayBuffer(file);
    toast.success(`${TOAST_MESSAGES.VALID_FILE_UPLOADED}: ${file.name}`);
    return {
      file,
      fileName: file.name,
      isValid: true,
      fileData: arrayBuffer,
      fileType: file.type,
    };
  } catch (error) {
    toast.error(TOAST_MESSAGES.FAILED_TO_READ_FILE);
    return null;
  }
};

export const processMultipleFiles = async (
  files: File[],
  allowedExtensions: string[]
): Promise<FileUploadState[]> => {
  const validFiles: FileUploadState[] = [];
  for (const file of files) {
    const processedFile = await processFileUpload(file, allowedExtensions, 'TTL file');
    if (processedFile) {
      validFiles.push(processedFile);
    }
  }
  if (validFiles.length > 0) {
    toast.success(`Added ${validFiles.length} TTL file(s)`);
  }
  return validFiles;
};
